import React, { useState, useRef, useEffect } from "react";
import ".././popup.common.css";
import styles from "./Style.module.css";
import BorderedButton from "../../Buttons/BorderedButton";
import PrimaryButton from "../../Buttons/PrimaryButton";
import CustomRadio from "../../CustomRadio/CustomRadio";
import { postMembershipResetPin } from "../../../APICalling/APICalling";
import { toast } from "react-toastify";
import {
	getMembershipPinInputProps,
	MembershipPinAutofillTrap,
} from "./membershipPinInput";

const PASSWORD_LENGTH = 4;

const FLOW_KNOWS = "knows";
const FLOW_FORGOT = "forgot";
const NEW_PASSWORD_SAME_AS_CURRENT_MSG =
	"New password must be different from the current password.";

const isPinComplete = (digits) => digits.every((digit) => digit !== "");

const ResetPasswordForMembership = ({
	onClose,
	onSubmitted,
	onResetSuccess,
	shouldContinueAfterReset = true,
	membershipId,
	initialFlowMode = FLOW_KNOWS,
}) => {
	const [passwordFlowMode, setPasswordFlowMode] = useState(
		initialFlowMode === FLOW_FORGOT ? FLOW_FORGOT : FLOW_KNOWS
	);
	const [oldDigits, setOldDigits] = useState(() => Array(PASSWORD_LENGTH).fill(""));
	const [newDigits, setNewDigits] = useState(() => Array(PASSWORD_LENGTH).fill(""));
	const [confirmDigits, setConfirmDigits] = useState(() => Array(PASSWORD_LENGTH).fill(""));
	const [overrideReason, setOverrideReason] = useState("");
	const oldRefs = useRef([]);
	const newRefs = useRef([]);
	const confirmRefs = useRef([]);
	const [submitError, setSubmitError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [fieldErrors, setFieldErrors] = useState({ old: "", new: "", confirm: "" });

	useEffect(() => {
		setOldDigits(Array(PASSWORD_LENGTH).fill(""));
		setNewDigits(Array(PASSWORD_LENGTH).fill(""));
		setConfirmDigits(Array(PASSWORD_LENGTH).fill(""));
		setOverrideReason("");
		setSubmitError("");
		setFieldErrors({ old: "", new: "", confirm: "" });
		const t = requestAnimationFrame(() => {
			if (passwordFlowMode === FLOW_KNOWS) {
				oldRefs.current[0]?.focus();
			} else {
				newRefs.current[0]?.focus();
			}
		});
		return () => cancelAnimationFrame(t);
	}, [passwordFlowMode]);

	useEffect(() => {
		setFieldErrors((prev) => {
			const shouldShowNewMismatch =
				passwordFlowMode === FLOW_KNOWS &&
				isPinComplete(oldDigits) &&
				isPinComplete(newDigits) &&
				oldDigits.join("") === newDigits.join("");

			if (shouldShowNewMismatch) {
				if (prev.new === NEW_PASSWORD_SAME_AS_CURRENT_MSG) return prev;
				return { ...prev, new: NEW_PASSWORD_SAME_AS_CURRENT_MSG };
			}

			if (prev.new === NEW_PASSWORD_SAME_AS_CURRENT_MSG) {
				return { ...prev, new: "" };
			}

			return prev;
		});
	}, [passwordFlowMode, oldDigits, newDigits]);

	const getRefs = (section) => {
		if (section === "old") return oldRefs;
		if (section === "new") return newRefs;
		return confirmRefs;
	};

	const handleChange = (section, index, value) => {
		setSubmitError((prev) => (prev ? "" : prev));
		setFieldErrors((prev) => {
			if (!prev.old && !prev.new && !prev.confirm) return prev;
			const next = { ...prev, [section]: "" };
			if (section === "old" || section === "new") {
				next.new = "";
			}
			return next;
		});
		const nextChar = (value || "").replace(/\D/g, "").slice(-1);
		const refs = getRefs(section);
		const setDigits =
			section === "old" ? setOldDigits : section === "new" ? setNewDigits : setConfirmDigits;

		setDigits((prev) => {
			const next = [...prev];
			next[index] = nextChar;
			return next;
		});

		if (nextChar && index < PASSWORD_LENGTH - 1) {
			refs.current[index + 1]?.focus();
		} else if (nextChar && index === PASSWORD_LENGTH - 1) {
			if (section === "old" && passwordFlowMode === FLOW_KNOWS) newRefs.current[0]?.focus();
			else if (section === "new") confirmRefs.current[0]?.focus();
		}
	};

	const handleKeyDown = (section, index, event) => {
		if (event.key === "Enter") {
			event.preventDefault();
			event.currentTarget?.form?.requestSubmit();
			return;
		}
		if (event.key !== "Backspace") return;
		const refs = getRefs(section);
		if (event.target.value === "" && index > 0) {
			event.preventDefault();
			refs.current[index - 1]?.focus();
			return;
		}
		if (event.target.value === "" && index === 0) {
			event.preventDefault();
			if (section === "new") {
				if (passwordFlowMode === FLOW_KNOWS) {
					oldRefs.current[PASSWORD_LENGTH - 1]?.focus();
				}
				return;
			}
			if (section === "confirm") newRefs.current[PASSWORD_LENGTH - 1]?.focus();
		}
	};

	const handlePaste = (section, event) => {
		event.preventDefault();
		setSubmitError((prev) => (prev ? "" : prev));
		const raw = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, PASSWORD_LENGTH);
		if (!raw) return;
		const refs = getRefs(section);
		const setDigits =
			section === "old" ? setOldDigits : section === "new" ? setNewDigits : setConfirmDigits;

		setDigits(() => {
			const next = Array(PASSWORD_LENGTH).fill("");
			for (let i = 0; i < raw.length; i += 1) {
				next[i] = raw[i];
			}
			return next;
		});
		requestAnimationFrame(() => {
			const focusAt = Math.min(raw.length, PASSWORD_LENGTH - 1);
			refs.current[focusAt]?.focus();
		});
	};

	const oldComplete = isPinComplete(oldDigits);
	const newComplete = isPinComplete(newDigits);
	const confirmComplete = isPinComplete(confirmDigits);
	const oldPin = oldDigits.join("");
	const newPin = newDigits.join("");
	const confirmPin = confirmDigits.join("");
	const passwordsMatch = newPin === confirmPin;
	const newMatchesCurrent =
		passwordFlowMode === FLOW_KNOWS && oldComplete && newComplete && oldPin === newPin;

	const hasRequiredFields =
		newComplete &&
		confirmComplete &&
		(passwordFlowMode === FLOW_FORGOT || (passwordFlowMode === FLOW_KNOWS && oldComplete));
	const canSubmit = hasRequiredFields;

	const handleSubmit = async (event) => {
		event?.preventDefault();
		if (isSubmitting) return;
		setSubmitError("");

		const nextFieldErrors = { old: "", new: "", confirm: "" };
		if (passwordFlowMode === FLOW_KNOWS && !oldComplete) {
			nextFieldErrors.old = "Please enter the current password.";
		}
		if (!newComplete) {
			nextFieldErrors.new = "Please enter a complete 4-digit new password.";
		} else if (newMatchesCurrent) {
			nextFieldErrors.new = NEW_PASSWORD_SAME_AS_CURRENT_MSG;
		}
		if (!confirmComplete) {
			nextFieldErrors.confirm = "Please confirm your new password.";
		} else if (newComplete && confirmComplete && !passwordsMatch) {
			nextFieldErrors.confirm =
				"The confirmation PIN does not match the new password. Please re-enter both fields.";
		}
		const hasAnyFieldError = Object.values(nextFieldErrors).some(Boolean);
		if (hasAnyFieldError) {
			setFieldErrors(nextFieldErrors);
			if (nextFieldErrors.old) oldRefs.current[0]?.focus();
			else if (nextFieldErrors.new) newRefs.current[0]?.focus();
			else confirmRefs.current[0]?.focus();
			return;
		}

		const id =
			membershipId != null && membershipId !== ""
				? Number(membershipId)
				: NaN;
		if (!Number.isFinite(id) || id <= 0) {
			setSubmitError("Membership is missing. Close and try again.");
			return;
		}
		setSubmitError("");
		setIsSubmitting(true);
		try {
			const payload = {
				current_pin: passwordFlowMode === FLOW_KNOWS ? oldDigits.join("") : "",
				new_pin: newPin,
				new_pin_confirmation: confirmPin,
				is_old_remeber: passwordFlowMode === FLOW_KNOWS ? 1 : 0,
			};
			if (passwordFlowMode === FLOW_FORGOT) {
				const reason = String(overrideReason || "").trim();
				if (reason) {
					payload.override_reason = reason;
				}
			}

			const setRes = await postMembershipResetPin(id, payload);
			const isSuccess = Boolean(setRes?.success || setRes?.data?.success);
			if (!isSuccess) {
				const apiMessage =
					setRes?.message || setRes?.data?.message || "Unable to reset password.";
				setSubmitError(apiMessage);
				return;
			}

			toast.success("Membership password updated successfully.");
			if (typeof onResetSuccess === "function") {
				onResetSuccess({
					is_old_remeber: passwordFlowMode === FLOW_KNOWS ? 1 : 0,
				});
			}
			if (shouldContinueAfterReset && typeof onSubmitted === "function") {
				onSubmitted();
			}
			if (typeof onClose === "function") {
				onClose();
			}
		} catch {
			setSubmitError("Network error. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	const formErrorMessage =
		submitError ||
		fieldErrors.confirm ||
		fieldErrors.new ||
		fieldErrors.old ||
		"";

	const renderPasswordSection = (section, label, digits, refs, hasError = false) => (
		<div className={styles.passwordSection}>
			<label className={styles.passwordSectionLabel}>{label}</label>
			<div className={styles.passwordRow} onPaste={(e) => handlePaste(section, e)}>
				{digits.map((value, index) => (
					<input
						key={`${section}-${index}`}
						ref={(el) => {
							refs.current[index] = el;
						}}
						{...getMembershipPinInputProps(`reset-${section}`, index)}
						maxLength={1}
						aria-label={`${label}: character ${index + 1} of ${PASSWORD_LENGTH}`}
						className={`form-control ${styles.passwordBox} ${hasError ? styles.passwordBoxError : ""}`}
						value={value}
						onChange={(e) => handleChange(section, index, e.target.value)}
						onKeyDown={(e) => handleKeyDown(section, index, e)}
					/>
				))}
			</div>
		</div>
	);

	return (
		<div className={`${styles.resetPasswordForMembership} popup`}>
			<div className="header">
				<h2>Reset Membership Password</h2>
			</div>

			<form className={styles.resetPasswordForm} autoComplete="off" onSubmit={handleSubmit}>
				<MembershipPinAutofillTrap />
				<div className="body">
				<div className={styles.resetPasswordModeList}>
					<CustomRadio
						label="Customer knows current password"
						name="resetMembershipPasswordFlow"
						value={FLOW_KNOWS}
						checked={passwordFlowMode === FLOW_KNOWS}
						onChange={(e) => setPasswordFlowMode(e.target.value)}
					/>
					<CustomRadio
						label="Customer does not remember current password"
						name="resetMembershipPasswordFlow"
						value={FLOW_FORGOT}
						checked={passwordFlowMode === FLOW_FORGOT}
						onChange={(e) => setPasswordFlowMode(e.target.value)}
					/>
				</div>

				{passwordFlowMode === FLOW_KNOWS &&
					renderPasswordSection("old", "Current Password", oldDigits, oldRefs, false)}
				{renderPasswordSection("new", "New Password", newDigits, newRefs, false)}
				{renderPasswordSection("confirm", "Confirm New Password", confirmDigits, confirmRefs, false)}
				<div className={styles.membershipPinFormError} role="alert" aria-live="polite">
					{formErrorMessage || null}
				</div>

				{passwordFlowMode === FLOW_FORGOT && (
					<div className={styles.overrideSection}>
						<label className={styles.overrideLabel} htmlFor="membership-password-override-reason">
							Enter reason for override (optional)
						</label>
						<textarea
							id="membership-password-override-reason"
							className={styles.overrideInput}
							value={overrideReason}
							onChange={(e) => setOverrideReason(String(e.target.value ?? "").slice(0, 150))}
							autoComplete="off"
							maxLength={150}
							rows={3}
						/>
					</div>
				)}

				</div>

				<div className="footer">
					<BorderedButton text="Cancel" onClick={onClose} type="button" />
					<PrimaryButton
						text={isSubmitting ? "Saving..." : "Reset Password"}
						type="submit"
						disabled={isSubmitting}
					/>
				</div>
			</form>
		</div>
	);
};

export default ResetPasswordForMembership;
